import { Component, EventEmitter, inject, signal, OnInit } from '@angular/core';
import { APIService } from 'src/app/services/APIService/api.service';
import { MrTranslatePipe } from '../../pipes/mr-translate.pipe';
import { CommonModule } from '@angular/common';
import { ClarityModule } from '@clr/angular';
import { AuthService } from 'src/app/services/Auth/auth.service';
import { Table } from 'src/app/models/ui/table/table';
import { SvelteGantt, SvelteGanttTable } from 'svelte-gantt';
import dayjs from 'dayjs';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { BackendLocaleDatePipe } from 'src/app/pipes/get-locale-date.pipe';
import { OverlayService } from 'src/app/services/Overlay/overlay.service';
import html2canvas from 'html2canvas';

interface TreeDataNode {
  id: number;
  name: string;
  data: any;
  expanded: boolean;
  lowestLevel: boolean;
  colour: string;
  typ: string;
  responsible?: string;
  kplace?: string;
  costs?: string;
  ktxt?: string;
  parent: TreeDataNode | null;
  children: TreeDataNode[];
}

@Component({
  selector: 'app-projekt',
  templateUrl: './projekt.component.html',
  styleUrls: ['./projekt.component.scss'],
  imports: [CommonModule, ClarityModule, MrTranslatePipe, FormsModule, ReactiveFormsModule],
})
export class ProjekteComponent implements OnInit {
  private readonly mrTranslate = inject(MrTranslatePipe);
  private readonly apiservice = inject(APIService);
  readonly authService = inject(AuthService);
  protected localeDate = inject(BackendLocaleDatePipe);

  sel: number;
  gantt: SvelteGantt;
  listTreeNote: TreeDataNode[] = [];
  root: TreeDataNode = undefined;
  modalOpen: boolean = false;
  curYear: number;
  stammdaten: any;
  curTask: any;
  dataRefresh = new EventEmitter();
  private readonly overlayService = inject(OverlayService);
  protected neuProjektFormGroup = new FormGroup({
    titel: new FormControl<string>(''),
    verantwortlich: new FormControl<string>('', [Validators.required]),
    kostenstelle: new FormControl<string>(''),
    projektstatus: new FormControl<string>('', [Validators.required]),
    projektbeginn: new FormControl<string>('', [Validators.required]),
    projektende: new FormControl<string>('', [Validators.required]),
    projektsumme: new FormControl<number>(0),
    kurztext: new FormControl<string>('', [Validators.required]),
    langtext: new FormControl<string>(''),
    bemerkung: new FormControl<string>(''),
    diagrammfarbe: new FormControl<string>(''),
    erledigt: new FormControl<boolean>(false),
    datum: new FormControl<string>(BackendLocaleDatePipe.today),
    lieferant: new FormControl<string>(''),
    kosten: new FormControl<number>(0),
  });

  protected readonly selected = signal<number>(undefined);
  protected readonly selectedParent = signal<number>(undefined);
  protected readonly selectedVon = signal<number>(undefined);
  protected readonly selectedBis = signal<number>(undefined);

  constructor() {
    this.dataRefresh.subscribe(() => {
      this.reloadProjects(this.curYear);
    });
  }

  years: number[];
  protected readonly table = signal<Table>({ rows: [], columns: [] });

  ngOnInit() {
    const tmpYear = new Date().getFullYear();
    this.curYear = tmpYear;
    this.sel = tmpYear;
    let von = new Date(tmpYear + '-01-01').valueOf();
    let bis = new Date(tmpYear + '-12-31').valueOf();
    let timeDiff = Math.abs(Math.round((bis - von) / (1000 * 60 * 60 * 24)));

    this.gantt = new SvelteGantt({
      target: document.getElementById('gantt'),
      props: {
        dateAdapter: {
          format(date, format) {
            return dayjs(date).format(format);
          },
          roundTo(date: number, unit: string, offset: number) {
            const magnetDuration = 1 * 24 * 60 * 60 * 1000;
            const value = Math.round(date / magnetDuration) * magnetDuration; //
            return value;
          },
        },
        tableHeaders: [
          { title: 'Projekte', property: 'label', width: 200, type: 'tree' },
          { title: 'Verantwortlich', property: 'responsible', width: 150 },
          { title: 'Kostenstelle', property: 'kplace', width: 150 },
          { title: 'Plankosten', property: 'costs', width: 100 },
          { title: 'Kurztext', property: 'ktxt', width: 150 },
          { title: 'von', property: 'fromUTC', width: 100 },
          { title: 'bis', property: 'toUTC', width: 100 },
        ],
        // tableHeaders: [{ title: 'Projekte', property: 'label', type: 'tree'}],
        columnUnit: 'day',
        columnOffset: 1,
        rowHeight: 52,
        rowPadding: 6,
        headers: [
          { unit: 'year', format: 'YYYY' },
          { unit: 'month', format: 'MMMM YYYY' },
          { unit: 'day', format: 'DD' },
        ],
        fitWidth: false,
        minWidth: timeDiff * 21,
        tableWidth: 600,
        reflectOnParentRows: false,
        ganttTableModules: [SvelteGanttTable],
        rows: [],
        tasks: [],
        from: von,
        to: bis,
        zoomLevels: [
          {
            headers: [
              { unit: 'month', format: 'MMM YYYY' },
              { unit: 'week', format: '[week] w' },
              { unit: 'day', format: 'DD' },
            ],
            minWidth: 8000,
            columnUnit: 'day',
            columnOffset: 1,
          },
        ],
      },
    });

    this.apiservice
      .getProjectYears()
      .pipe()
      .subscribe(res => {
        this.years = res;
        this.reloadProjects(this.curYear);
      });
  }

  public reloadProjects(year: number) {
    this.apiservice
      .getProject(year)
      .pipe()
      .subscribe(res => {
        this.rebuildGantt(res, year);
      });
  }

  public rebuildGantt(res, year: number) {
    let rows = [];
    let tasks = [];
    let count: number = 1;
    this.listTreeNote = [];
    let route = JSON.parse(sessionStorage.getItem('treeRoute') ?? '{}');

    let upperroot: TreeDataNode = {
      id: -1,
      name: 'Projekte',
      data: null,
      expanded: null,
      colour: null,
      typ: 'blank',
      lowestLevel: false,
      parent: null,
      children: [],
    };
    let idRoot: number = 0;
    for (let obj of res.rows) {
      idRoot = idRoot + 1;
      if (obj['Title'] != '' && obj['Title'] != null && obj['ParentID'] <= 0) {
        let extend: boolean = route && route.length > 0 ? route[0] == upperroot.name : false;

        this.root = {
          id: obj['ID'],
          name: obj['Title'],
          data: obj,
          colour: obj['ColorCode'].toString(16).padStart(6, '0'),
          expanded: extend,
          typ: 'blank',
          lowestLevel: false,
          parent: null,
          children: [],
        };
        upperroot.children.push(this.root);
        this.FillChildren(this.root, res.rows, 1, obj['ID'], route);

        let childrenGantt = [];
        let idChildren: number = 1;

        this.root.children.forEach(element => {
          childrenGantt.push({
            id: idRoot + '' + idChildren,
            label: element.name,
            realID: element.id,
            taskid: count,
            responsible: element.responsible,
            realParentID: element.parent.id,
            from: new Date(element.data.ProjectStart).valueOf(),
            to: new Date(element.data.ProjectEnde).valueOf(),
            fromUTC: new Date(element.data.ProjectStart).toISOString().split('T')[0],
            toUTC: new Date(element.data.ProjectEnde).toISOString().split('T')[0],
            kplace: obj['Kostenstelle'],
            ktxt: obj['Kurztext'],
            costs: obj['Plankosten'],
          });
          tasks.push({
            id: count,
            resourceId: parseInt(idRoot + '' + idChildren),
            label: element.name,
            realID: element.id,
            styles: element.colour != '000000' ? 'background:#' + element.colour : '',
            realParentID: element.parent.id,
            from: new Date(element.data.ProjectStart).valueOf(),
            to: new Date(element.data.ProjectEnde).valueOf(),
          });
          idChildren = idChildren + 1;
          count = count + 1;
        });

        if (childrenGantt.length > 0) {
          rows.push({
            id: parseInt(idRoot + '0'),
            label: obj['Title'],
            realID: obj['ID'],
            taskid: count,
            realParentID: obj['ParentID'],
            from: new Date(obj['ProjectStart']).valueOf(),
            to: new Date(obj['ProjectEnde']).valueOf(),
            fromUTC: new Date(obj['ProjectStart']).toISOString().split('T')[0],
            toUTC: new Date(obj['ProjectEnde']).toISOString().split('T')[0],
            kplace: obj['Kostenstelle'],
            ktxt: obj['Kurztext'],
            costs: obj['Plankosten'],
            responsible: obj['Manager'],
            class: 'row-group',
            children: childrenGantt,
            contentHtml: "<button class='nav-link nav-icon cursor-pointer' clrDropdownTrigger ></button>",
          });
        } else {
          rows.push({
            id: parseInt(idRoot + '0'),
            label: obj['Title'],
            realID: obj['ID'],
            taskid: count,
            realParentID: obj['ParentID'],
            from: new Date(obj['ProjectStart']).valueOf(),
            to: new Date(obj['ProjectEnde']).valueOf(),
            fromUTC: new Date(obj['ProjectStart']).toISOString().split('T')[0],
            toUTC: new Date(obj['ProjectEnde']).toISOString().split('T')[0],
            kplace: obj['Kostenstelle'],
            ktxt: obj['Kurztext'],
            costs: obj['Plankosten'],
            responsible: obj['Manager'],
          });
        }

        tasks.push({
          id: count,
          resourceId: parseInt(idRoot + '0'),
          realID: obj['ID'],
          realParentID: obj['ParentID'],
          label: obj['Title'],
          styles: obj['ColorCode'] ? 'background:#' + obj['ColorCode'].toString(16).padStart(6, '0') : '',
          from: new Date(obj['ProjectStart']).valueOf(),
          to: new Date(obj['ProjectEnde']).valueOf(),
        });
        count = count + 1;

        this.listTreeNote.push(this.root);
      }
    }

    let von = new Date(year + '-01-01').valueOf();
    let bis = new Date(year + '-12-31').valueOf();
    let timeDiff = Math.abs(Math.round((bis - von) / (1000 * 60 * 60 * 24)));
    let dates = tasks.map(x => [x.id, x.from]).sort((a, b) => a[1] - b[1]);

    this.gantt.$set({
      minWidth: timeDiff * 21,
      ganttTableModules: [SvelteGanttTable],
      rows: rows,
      tasks: tasks,
      from: von,
      to: bis,
    });
    setTimeout(() => {
      this.gantt.scrollToTask(dates.length > 0 ? dates[0][0] : 1, 'smooth');
      this.gantt.api.tasks.on.select(x => {
        this.curTask = x[0].model;
        this.selected.set(this.curTask.realID);
        this.selectedParent.set(this.curTask.realParentID);
        this.selectedVon.set(this.curTask.from);
        this.selectedBis.set(this.curTask.to);
      });
      this.gantt.api.rows.on.select(x => {
        this.curTask = x[0].model;
        this.gantt.scrollToTask(this.curTask.taskid, 'smooth');
        this.selected.set(this.curTask.realID);
        this.selectedParent.set(this.curTask.realParentID);
        this.selectedVon.set(this.curTask.from);
        this.selectedBis.set(this.curTask.to);
      });

      this.gantt.api.tasks.on.changed(x => {
        if (x) this.curTask = x[0].task.model;
        this.selected.set(this.curTask.realID);
        this.selectedParent.set(this.curTask.realParentID);
        this.selectedVon.set(this.curTask.from);
        this.selectedBis.set(this.curTask.to);
        let c = {
          ID: this.selected(),
          projektbeginn: new Date(this.selectedVon()).toISOString(),
          projektende: new Date(this.selectedBis()).toISOString(),
        };
        this.apiservice
          .setUpdateProject(c)
          .pipe()
          .subscribe(res => {
            this.reloadProjects(this.curYear);
          });
      });
    }, 20);
  }

  public getChildren = node => node.children;

  public FillChildren(parentNode: TreeDataNode, rows: any, level: number, searchFor: string, route: string[]) {
    const filtered = rows.filter(obj => {
      return obj['ParentID'] === searchFor && searchFor;
    });

    let goFor = filtered;
    for (const obj of goFor) {
      let name = obj['Title'];
      let extend: boolean = route && route.length >= level ? route[level] == name : false;

      let node: TreeDataNode = {
        id: obj['ID'],
        name: name,
        data: obj,
        expanded: extend,
        responsible: obj['Manager'],
        kplace: obj['Kostenstelle'],
        ktxt: obj['Kurztext'],
        costs: obj['Plankosten'],
        colour: obj['ColorCode'].toString(16).padStart(6, '0'),
        typ: 'unknown-status',
        lowestLevel: false,
        parent: parentNode,
        children: [],
      };
      parentNode.children.push(node);
      level = level + 1;
      this.FillChildren(node, filtered, level, obj['ID'], route);
      level = level - 1;
    }
  }
  public getUpperDef(arr: string[], node: TreeDataNode): string[] {
    if (node.parent) {
      arr.push(node.parent.name);
      this.getUpperDef(arr, node.parent);
    }
    return arr;
  }

  selYearChanged(year) {
    this.curYear = year;
    this.reloadProjects(year);
  }

  newProject() {
    this.overlayService.setOverlay({
      overlay: 'projektDialog',
      data: {
        selID: -99,
        mode: 'new',
      },
      zIndex: 8,
      dataRefresh: this.dataRefresh,
    });
  }
  newSubProject() {
    this.overlayService.setOverlay({
      overlay: 'projektDialog',
      data: {
        selID: -99,
        parentID: this.selected(),
        limitVon: this.selectedVon(),
        limitBis: this.selectedBis(),
        mode: 'newSub',
      },
      zIndex: 8,
      dataRefresh: this.dataRefresh,
    });
  }
  openProject() {
    this.overlayService.setOverlay({
      overlay: 'projektDialog',
      data: {
        selID: this.selected(),
        parentID: this.selectedParent(),
        mode: 'open',
      },
      zIndex: 8,
      dataRefresh: this.dataRefresh,
    });
  }
  deleteProject() {
    const text = this.mrTranslate.transform('Projekt wirklich löschen?');
    if (confirm(text)) {
      this.apiservice
        .getDeleteProject(this.selected())
        .pipe()
        .subscribe(res => {
          this.reloadProjects(this.curYear);
        });
    }
  }
  capture() {
    html2canvas(document.querySelector('#gantt')).then(canvas => {
      document.body.appendChild(canvas);
      let link = document.createElement('a');
      link.download = 'screenshot_' + dayjs().format('YYYY-MM-DDTHH:mm:ss') + '.png';
      link.href = canvas.toDataURL();
      link.click();
    });
  }
}
